import { createContext, useCallback, useContext, useEffect, useState, useRef } from 'react';
import {
  InventoryContextData,
  InventoryData,
  InventoryLocationState,
  InventoryProcessingData,
  InventoryProviderProps,
  InventoryToggle,
} from './types';
import { initialState } from './constants';
import { InventoryCreateRoutePointReq } from '@services/inventory/points/types';
import { useQueryParams } from '@hooks/queryParams';
import { useCreateInventoryRoutePoint } from '@services/inventory/points/create';
import { useToast } from '@hooks/useToast';
import { canScheduleRoute } from './helpers';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useGetInventoryRoutes } from '@services/inventory/routes';
import { useCreateInventoryRoute } from '@services/inventory/routes/create';
import { useRemoveInventoryRoute } from '@services/inventory/routes/remove';
import { useClearAllInventoryRoutePoints } from '@services/inventory/points/clearAll';
import { useUpdateInventoryRoutePoint } from '@services/inventory/points/update';
import { useGetInventoryAvailable } from '@services/inventory/available';
import { useGetInventoryRequested } from '@services/inventory/requested';
import { useGetInventoryRequestedLocation } from '@services/inventory/requested/location';
import { useGetInventoryAvailableLocation } from '@services/inventory/available/location';
import { useCreateInventoryRoutePointsPair } from '@services/inventory/pair/create';
import { CreateInventoryRoutePointsPairReq } from '@services/inventory/pair/create/types';
import { InventoryLocation } from '@root/globalTypes';
import { useRemoveInventoryRoutePointsPair } from '@services/inventory/pair/remove';
import { useDropdownsContext } from '@contexts/dropdowns';
import { InventoryAdditionalItemsConfirmationDialogOption } from '@components/inventory/dialogs/confirmation/additionalItems/options/types';

const InventoryContext = createContext<InventoryContextData | Record<string, never>>({});

export const InventoryProvider = ({ children }: InventoryProviderProps) => {
  const {
    routes: { options, disabled: routesLoading },
  } = useDropdownsContext();

  const {
    params: { routeId },
    setParams,
  } = useQueryParams();
  const location = useLocation();
  const openSchedule = Boolean((location.state as InventoryLocationState | null)?.openSchedule);
  const handledNavigation = useRef('');

  const { toast } = useToast();
  const { t } = useTranslation('app', { keyPrefix: 'messages.success.inventory' });
  const { t: tInventory } = useTranslation('app', { keyPrefix: 'pages.inventory' });
  const [state, setState] = useState<InventoryData>(initialState);

  const {
    dialog: {
      confirmation,
      customAdd: { data: customItems, unpackDelivery },
    },
    processing: {
      type,
      data: { locationId, locationType },
      extraDropOffNeeded,
    },
  } = state;

  const endProcess = (key: string) => {
    toast({ title: t(key) });
    setState((prev) => ({
      ...prev,
      processing: initialState.processing,
      dialog: initialState.dialog,
    }));
  };

  const requested = useGetInventoryRequested({ location: state.processing.data, customItems });
  const requestedLocation = useGetInventoryRequestedLocation({
    preview: { type, locationId, locationType },
    customItems,
  });

  const available = useGetInventoryAvailable({ location: state.processing.data, customItems });
  const availableLocation = useGetInventoryAvailableLocation({
    preview: { type, locationId, locationType },
    customItems,
  });

  const routesResp = useGetInventoryRoutes();
  const routes = routesResp.data?.data;

  const createRoute = useCreateInventoryRoute({
    callback: () => endProcess('routes.create'),
  });

  const removeRoute = useRemoveInventoryRoute({
    callback: () => endProcess('routes.remove'),
  });

  const createPoint = useCreateInventoryRoutePoint({
    location: state.processing.data,
    callback: () => endProcess('create'),
  });

  const updatePoint = useUpdateInventoryRoutePoint({
    location: state.processing.data,
    callback: () => endProcess('update'),
  });

  const clearAllPoints = useClearAllInventoryRoutePoints({
    callback: () => endProcess('clearAll'),
  });

  const createPair = useCreateInventoryRoutePointsPair({
    location: state.processing.data,
    callback: () => endProcess('pair.create'),
  });

  const removePair = useRemoveInventoryRoutePointsPair({
    callback: () => endProcess('pair.remove'),
  });

  const toggle = (prop: InventoryToggle, open: boolean) =>
    setState((prev) => ({
      ...prev,
      dialog: { ...prev.dialog, [prop]: { ...prev.dialog[prop], open } },
    }));

  const handleCreateExtraDropOff = (location: InventoryLocation) => {
    if (!routeId) return;
    const values: InventoryCreateRoutePointReq = {
      predefined: true,
      predefinedType: 'extra_drop',
      deliveryType: 'drop_off',
      locationId: location.locationId,
      locationType: location.locationType,
      routeId,
    };
    createPoint.mutate(values);
  };

  const handleCreatePair = (location: InventoryLocation) => {
    const values: CreateInventoryRoutePointsPairReq = {
      dropOffLocationId: type === 'requested' ? locationId : location.locationId,
      dropOffLocationType: type === 'requested' ? locationType : location.locationType,
      pickUpLocationId: type === 'requested' ? location.locationId : locationId,
      pickUpLocationType: type === 'requested' ? location.locationType : locationType,
      skipExcess: confirmation.value === 'skipExcess',
      includeAllInCurrentDrop: confirmation.value === 'includeAllInCurrentDrop',
      createExtraDrop: confirmation.value === 'createExtraDrop',
      dropOffCustomItems: type === 'requested' ? customItems : undefined,
      pickUpCustomItems: type === 'available' ? customItems : undefined,
      unpackDelivery,
    };
    createPair.mutate(values);
  };

  const handleCreate = (location: InventoryLocation) => {
    if (extraDropOffNeeded) return handleCreateExtraDropOff(location);
    return handleCreatePair(location);
  };

  const handleClose = () => {
    if (extraDropOffNeeded) toggle('remove', true);
    else
      setState((prev) => ({
        ...prev,
        processing: initialState.processing,
        dialog: { ...prev.dialog, customAdd: initialState.dialog.customAdd },
      }));
  };

  const setProcessing = useCallback((): InventoryProcessingData => {
    const point = routes?.points.flat().find((el) => el.hidden && el.deliveryType === 'pick_up');
    const data = available?.data?.records.find(
      (el) => el.locationId === point?.job?.id || point?.warehouse?.id,
    );

    if (!point || !data) return state.processing;

    return {
      active: true,
      type: 'available',
      data,
      extraDropOffNeeded: true,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes?.points, available?.data?.records]);

  const closeConfirmationDialog = () => {
    setState((prev) => ({
      ...prev,
      dialog: { ...prev.dialog, confirmation: initialState.dialog.confirmation },
    }));
  };

  const setConfirmationDialogValue = (value: InventoryAdditionalItemsConfirmationDialogOption) => {
    setState((prev) => ({
      ...prev,
      dialog: {
        ...prev.dialog,
        confirmation: {
          ...prev.dialog.confirmation,
          value,
        },
      },
    }));
  };

  useEffect(() => {
    setState((prev) => ({ ...prev, routeId: routeId || '', processing: initialState.processing }));
  }, [routeId]);

  useEffect(() => {
    setState((prev) => ({ ...prev, processing: setProcessing() }));
  }, [setProcessing]);

  useEffect(() => {
    if (!routes) return;
    setState((prev) => ({ ...prev, routes, draft: Boolean(!routes.scheduleDate) }));
  }, [routes]);

  useEffect(() => {
    if (!routeId || routesLoading) return;
    const clearState = { replace: true, state: null };
    const fresh = openSchedule && handledNavigation.current !== location.key;
    if (!options.some((el) => el.id === routeId)) {
      if (fresh) toast({ title: tInventory('routeUnavailable'), variant: 'destructive' });
      handledNavigation.current = location.key;
      return setParams({ routeId: undefined }, clearState);
    }
    if (!openSchedule || routes?.id !== routeId) return;
    if (fresh && canScheduleRoute(routes.points)) toggle('schedule', true);
    handledNavigation.current = location.key;
    setParams({}, { ...clearState, disableSaveFilters: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, routeId, routesLoading, openSchedule, routes]);

  return (
    <InventoryContext.Provider
      value={{
        ...state,
        setState,
        toggle,
        createRoute,
        removeRoute,
        updatePoint,
        clearAllPoints,
        removePair,
        create: {
          mutate: handleCreate,
          isLoading: createPoint.isLoading || createPair.isLoading,
        },
        handleClose,
        setConfirmationDialogValue,
        closeConfirmationDialog,
        requested: {
          isLoading: requested.isLoading,
          data: requested.data?.records || [],
        },
        available: {
          isLoading: available.isLoading,
          data: available.data?.records || [],
        },
        items: {
          isLoading: requestedLocation.isLoading || availableLocation.isLoading,
          data: requestedLocation.data?.records || availableLocation.data?.records || [],
          multiload:
            requestedLocation.data?.multiload || availableLocation.data?.multiload || false,
        },
      }}>
      {children}
    </InventoryContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useInventoryContext = () => useContext(InventoryContext);
